import { useFlags } from "launchdarkly-react-client-sdk";
import { useCallback, useState } from "react";

import { useAppDispatch } from "app/hooks";
import {
  setCustomMessage,
  setOpen
} from "components/action-snackbar/action-snackbar-slice";
import ErrorSnackbarContent from "components/error-snackbar-content";
import { PAYWALL_BYPASS } from "constants/feature-flags";
import { AMBIENT_SCRIBE_FEATURE_KEY } from "constants/paywall";
import { showFeatureDialog } from "features/auth/auth-slice";
import {
  useGenerateAmbientSoapNotesMutation,
  useGenerateAmbientSummaryMutation,
  useGenerateTranscriptionMutation
} from "services/ambient-scribe";
import { useLazyGetCurrentUserPaywallFeaturesQuery } from "services/paywall";

import type { GenerationKind } from "./use-scribe-generation-latches.hook";

export type GenerateError =
  | { kind: "subscription"; error: string; message: string }
  | { kind: "generic" };

/** The summary's 402 carries the subscription's own words; anything else is a generic failure. */
function toSummaryGenerateError(error: unknown): GenerateError {
  const err = error as {
    status?: number;
    data?: { subscription_state?: unknown; error: string; message: string };
  };
  if (err?.status === 402 && err?.data?.subscription_state) {
    return {
      kind: "subscription",
      error: err.data.error,
      message: err.data.message
    };
  }
  return { kind: "generic" };
}

/**
 * The details' own Generate buttons (SOAP, summary, transcript). Each checks the paywall first,
 * holds its latch while the request runs, and lets go of it if the request fails.
 */
export default function useScribeGenerateActions({
  scribeId,
  setLatch
}: {
  scribeId: number | undefined;
  setLatch: (kind: GenerationKind, isHeld: boolean) => void;
}) {
  const flags = useFlags();
  const dispatch = useAppDispatch();
  const paywallBypassEnabled = flags[PAYWALL_BYPASS] ?? true;
  const [generateSoapNotes] = useGenerateAmbientSoapNotesMutation();
  const [generateSummary] = useGenerateAmbientSummaryMutation();
  const [generateTranscription] = useGenerateTranscriptionMutation();
  const [fetchPaywallFeatures] = useLazyGetCurrentUserPaywallFeaturesQuery();
  const [summaryGenerateError, setSummaryGenerateError] =
    useState<GenerateError | null>(null);

  const checkPaywall = useCallback(async () => {
    const response = await fetchPaywallFeatures();
    const isActive = Boolean(
      response.data?.[AMBIENT_SCRIBE_FEATURE_KEY]?.active
    );
    if (!paywallBypassEnabled && !isActive) {
      dispatch(showFeatureDialog(AMBIENT_SCRIBE_FEATURE_KEY));
      return false;
    }
    return true;
  }, [fetchPaywallFeatures, paywallBypassEnabled, dispatch]);

  const handleGenerateSoap = useCallback(async () => {
    if (!(await checkPaywall())) return;
    setLatch("soap", true);
    const result = await generateSoapNotes({ id: Number(scribeId) });
    if ("error" in result) {
      setLatch("soap", false);
      dispatch(setCustomMessage(<ErrorSnackbarContent response={result} />));
      dispatch(setOpen(true));
    }
  }, [scribeId, checkPaywall, generateSoapNotes, setLatch, dispatch]);

  const handleGenerateSummary = useCallback(async () => {
    if (!(await checkPaywall())) return;
    setLatch("summary", true);
    const result = await generateSummary({ id: Number(scribeId) });
    if ("error" in result) {
      setLatch("summary", false);
      setSummaryGenerateError(toSummaryGenerateError(result.error));
    }
  }, [scribeId, checkPaywall, generateSummary, setLatch]);

  const handleGenerateTranscript = useCallback(async () => {
    if (!(await checkPaywall())) return;
    setLatch("transcript", true);
    const result = await generateTranscription({ id: Number(scribeId) });
    if ("error" in result) {
      setLatch("transcript", false);
      dispatch(setCustomMessage(<ErrorSnackbarContent response={result} />));
      dispatch(setOpen(true));
    }
  }, [scribeId, checkPaywall, generateTranscription, setLatch, dispatch]);

  const clearSummaryGenerateError = useCallback(
    () => setSummaryGenerateError(null),
    []
  );

  return {
    handleGenerateSoap,
    handleGenerateSummary,
    handleGenerateTranscript,
    summaryGenerateError,
    clearSummaryGenerateError
  };
}
